'use client'
import { Input, Text, TextInput, type TextInputProps } from '@mantine/core'
import { RefObject, useContext, useEffect, useRef, useState } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { useJsApiLoader, Libraries } from '@react-google-maps/api'

import { CommonFieldLabel } from '~/app/(me)/_components'

import { appTrace } from '~/utils'

import { formErrorText, textInputRoot } from '../styles.css'
import { AppLayoutContext } from '~/app/_components'

// WARNING careful - minor changes to this widget can trigger browser crashes
export function CommonLocationField(props: TextInputProps & { name: string }) {
  const fieldName = props.name
  const { mobile } = useContext(AppLayoutContext)

  return (
    <Input.Label fz={mobile ? '13px' : 'h4'} fw={mobile ? 600 : 600} w="100%">
      <CommonFieldLabel name={fieldName} label="LOCATION" />
      <CommonLocationFieldAutocomplete {...props} />
    </Input.Label>
  )
}

const libraries: Libraries = ['places']

export function useLocationAutocomplete(
  inputRef: RefObject<HTMLInputElement | null>,
  onChange: (value: { formatted_address: string }) => void
) {
  if (process.env.NEXT_PUBLIC_CI) return

  // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyBkXCw8fd_BRfOViPsgVTsOa9QIpDOCvHo',
    libraries,
  })

  // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
  useEffect(() => {
    if (isLoaded && window.google && inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['(cities)'],
      })

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        if (place.formatted_address) {
          onChange({ formatted_address: place.formatted_address })
        }
      })
    }
  }, [isLoaded])

  // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
  useEffect(() => {
    if (loadError) throw new Error('Failed to load Google Maps API')
  }, [loadError])
}

function CommonLocationFieldAutocomplete(props: TextInputProps & { name: string }) {
  const fieldName = props.name
  const profileForm = useFormContext()
  // Subscribe to the field's value changes
  const value = useWatch({
    control: profileForm.control,
    name: fieldName,
  })

  const [tempInput, setTempInput] = useState('')

  const { field, fieldState } = useController({ name: fieldName })

  // Add effect to handle form initialization
  useEffect(() => {
    if (value && !field.value) {
      field.onChange(value)
    }
  }, [value, field])

  return (
    <>
      <CommonAutocompleteInput
        w="100%"
        size="md"
        value={tempInput || field.value}
        onChange={(input) => {
          setTempInput(input.target.value) // Update the temporary input
          field.onChange(input.target.value) // Also update the form value
        }}
        onPlaceSelected={(place) => {
          if (place.formatted_address) {
            field.onChange(place.formatted_address) // Save only valid location
            setTempInput(place.formatted_address) // Reflect the selected location in the input
          }
        }}
        classNames={{ input: textInputRoot }}
        {...props}
      />
      <input
        type="hidden"
        data-testid="location"
        onChange={(event) => {
          appTrace('changing location', event.target.value)
          field.onChange(event.target.value)
        }}
      />
      {fieldState.error ? <Text className={formErrorText}>{fieldState.error.message}</Text> : undefined}
    </>
  )
}

export function CommonAutocompleteInput(
  props: TextInputProps & { autoclear?: boolean; onPlaceSelected: (value: { formatted_address: string }) => void }
) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { onPlaceSelected, autoclear, ...restProps } = props
  useLocationAutocomplete(inputRef, (place) => {
    onPlaceSelected(place)

    // Clear the input field after selecting a place
    if (autoclear) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      }, 100)
    }
  })

  return <TextInput w="100%" type="text" autoCapitalize="true" ref={inputRef} {...restProps} />
}
